import React, { useEffect, useState } from "react";
import axios from "axios";
import { config } from "../../../config";
import { Link } from "react-router-dom";
import UpdateProduct from "./UpdateProduct";
import imageCompression from "browser-image-compression";

const apiUrl = config.apiUrl;

const ViewProduct = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    discount_price: "",
    category_id: "",
    clothing: false,
    images: [],
    colors: [{ color: "", image: null, sizes: [{ size: "" }] }],
    bulk_discounts: [{ title: "", offer_quantity: "", discount_percentage: "" }],
    bumps: [{ title: "", bump_price: "", image: null, description: "" }],
    homepage: {
      headline: "",
      paragraph: "",
      description: ""
    },
    singleProductSizes: [{ size: "" }]
  });
  const [showHomepageFields, setShowHomepageFields] = useState(false);
  const [showBulkDiscounts, setShowBulkDiscounts] = useState(false);
  const [showBumps, setShowBumps] = useState(false);
  const [showSingleProductSizes, setShowSingleProductSizes] = useState(false);

  const imageCompressionOptions = {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 1024,
    useWebWorker: true,
    fileType: "image/webp"
  };

  console.log("formData:", formData);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${apiUrl}/products`),
          axios.get(`${apiUrl}/category`)
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data.categories || []);
      } catch (err) {
        setError("Failed to load products and categories");
        console.error("Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await axios.delete(`${apiUrl}/productdelete/${id}`);
        setProducts(prev => prev.filter(product => product.id !== id));
      } catch (err) {
        setError("Failed to delete product");
        console.error("Error:", err?.response?.data?.message || err.message);
      }
    }
  };

  const handleEditClick = async (product) => {
    setEditingProduct(product.id);
    setFormData({
      slug: product.slug || "",
      name: product.name || "",
      price: product.price || "",
      discount_price: product.discount_price || "",
      category_id: product.category_id || "",
      clothing: product.clothing || false,
      images: product.images || [],
      colors: product.colors?.map(color => ({
        id: color.id || null,
        color: color.color || "",
        image: color.image || null,
        sizes: color.sizes?.map(size => ({
          id: size.id || null,
          size: size.size || ""
        })) || []
      })) || [],
      bulk_discounts: product.bulk_discounts?.map(discount => ({
        id: discount.id || null,
        title: discount.title || "",
        offer_quantity: discount.offer_quantity || "",
        discount_percentage: discount.discount_percentage || ""
      })) || [],
      bumps: product.bumps?.map(bump => ({
        id: bump.id || null,
        title: bump.title || "",
        bump_price: bump.bump_price || "",
        image: bump.image || null,
        description: bump.description || ""
      })) || [],

      singleProductSizes: product.single_product_sizes?.map(size => ({
        id: size.id,
        size: size.size || ""
      })) || [],

      homepage: product.homepage || {
        headline: "",
        paragraph: "",
        description: ""
      }
    });
    setShowHomepageFields(!!product.homepage);
    setShowBulkDiscounts(product.bulk_discounts?.length > 0);
    setShowBumps(product.bumps?.length > 0);
    setShowSingleProductSizes(product.single_product_sizes?.length > 0);
  };

  const handleBumpImageChange = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressedBlob = await imageCompression(file, imageCompressionOptions);
      const compressedFile = new File(
        [compressedBlob],
        file.name.replace(/\.[^/.]+$/, ".webp"),
        { type: "image/webp", lastModified: Date.now() }
      );

      const updatedBumps = [...formData.bumps];
      updatedBumps[index] = {
        ...updatedBumps[index],
        image: compressedFile
      };

      setFormData(prev => ({
        ...prev,
        bumps: updatedBumps
      }));
    } catch (error) {
      console.error("Image compression failed:", error);
    }
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    
    try {
      const compressedFiles = await Promise.all(
        files.map(async (file) => {
          return await imageCompression(file, imageCompressionOptions);
        })
      );
      
      setFormData(prev => ({ ...prev, images: compressedFiles }));
    } catch (error) {
      console.error("Image compression error:", error);
      setError("Failed to compress images");
    }
  };

  const handleColorImageChange = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressedFile = await imageCompression(file, imageCompressionOptions);
      const colors = [...formData.colors];
      colors[index].image = compressedFile;
      setFormData(prev => ({ ...prev, colors }));
    } catch (error) {
      console.error("Color image compression error:", error);
      setError("Failed to compress color image");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append("_method", "PUT");
      data.append("name", formData.name);
      data.append("price", formData.price);
      data.append("discount_price", formData.discount_price || "");
      data.append("category_id", formData.category_id);
      data.append("slug", formData.slug);
      data.append("clothing", formData.clothing ? "1" : "0");

      if (showHomepageFields) {
        data.append("homepage[headline]", formData.homepage.headline || "");
        data.append("homepage[paragraph]", formData.homepage.paragraph || "");
        data.append("homepage[description]", formData.homepage.description || "");
      }

      if (showBulkDiscounts && formData.bulk_discounts.length > 0) {
        formData.bulk_discounts.forEach((discount, index) => {
          if (discount.id) data.append(`bulk_discounts[${index}][id]`, discount.id);
          data.append(`bulk_discounts[${index}][title]`, discount.title || "");
          data.append(`bulk_discounts[${index}][offer_quantity]`, discount.offer_quantity || "");
          data.append(`bulk_discounts[${index}][discount_percentage]`, discount.discount_percentage || "");
        });
      }

      if (showBumps && formData.bumps.length > 0) {
        formData.bumps.forEach((bump, index) => {
          if (bump.id) data.append(`bumps[${index}][id]`, bump.id);
          data.append(`bumps[${index}][title]`, bump.title || "");
          data.append(`bumps[${index}][bump_price]`, bump.bump_price || "");
          data.append(`bumps[${index}][description]`, bump.description || "");
          if (bump.image instanceof File) {
            data.append(`bumps[${index}][image]`, bump.image);
          } else if (typeof bump.image === 'string') {
            data.append(`bumps[${index}][existing_image]`, bump.image);
          }
        });
      }

     if (showSingleProductSizes && formData.singleProductSizes.length > 0) {
        formData.singleProductSizes.forEach((size, index) => {
          if (size.id) {
            data.append(`singleProductSizes[${index}][id]`, size.id); // ✅ Send ID for existing sizes
          }
          data.append(`singleProductSizes[${index}][size]`, size.size || "");
        });
      }

      if (!formData.clothing && formData.images.length > 0) {
        formData.images.forEach((image, index) => {
          if (image instanceof File) {
            data.append(`images[${index}]`, image);
          }
        });
      }

      if (formData.colors.length > 0) {
        formData.colors.forEach((color, index) => {
          if (color.id) data.append(`colors[${index}][id]`, color.id);
          data.append(`colors[${index}][color]`, color.color || "");
          if (color.image instanceof File) {
            data.append(`colors[${index}][image]`, color.image);
          }
          color.sizes.forEach((size, sizeIndex) => {
            if (size.id) data.append(`colors[${index}][sizes][${sizeIndex}][id]`, size.id);
            data.append(`colors[${index}][sizes][${sizeIndex}][size]`, size.size || "");
          });
        });
      }

      await axios.post(`${apiUrl}/productsupdate/${editingProduct}`, data, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      alert("Product updated successfully!");
      const response = await axios.get(`${apiUrl}/products`);
      setProducts(response.data);
      setEditingProduct(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update product");
      console.error("Error:", err);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading products...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6 text-center">Product List</h2>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {[...products].reverse().map(product => (
              <tr key={product.id}>
                <td className="px-6 py-4">
                  <div className="font-medium">{product.name}</div>
                  <div className="text-sm text-gray-500">{product.slug}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-red-500 line-through">৳{product.price}</div>
                  {product.discount_price && (
                    <div>৳{product.discount_price}</div>
                  )}
                </td>
                <td className="px-6 py-4">
                  {categories.find(c => c.id === product.category_id)?.name || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap space-x-2">
                  <button
                    onClick={() => handleEditClick(product)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(product.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                  <Link 
                    to={`/${product.slug}`} 
                    className="text-green-600 hover:text-green-900"
                  >
                    View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingProduct && (
        <UpdateProduct
          formData={formData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          onCancel={() => setEditingProduct(null)}
          categories={categories}
          showHomepageFields={showHomepageFields}
          setShowHomepageFields={setShowHomepageFields}
          showBumps={showBumps}
          setShowBumps={setShowBumps}
          handleImageChange={handleImageChange}
          handleColorImageChange={handleColorImageChange}
          handleBumpImageChange={handleBumpImageChange}
          showSingleProductSizes={showSingleProductSizes}
          setShowSingleProductSizes={setShowSingleProductSizes}
        />
      )}
    </div>
  );
};

export default ViewProduct;